import { config, IBasemapTileWMS, IBasemapTileXYZ } from "./config";
import { Feature, View, Map } from "ol";
import { Vector as VectorSource, TileWMS, XYZ, ImageArcGISRest } from "ol/source";
import { Tile as TileLayer, Vector as VectorLayer, Image as ImageLayer } from "ol/layer";
import TileGrid from "ol/tilegrid/TileGrid";
import { defaults as defaultControls } from "ol/control";
import { Basemaps } from "./enums";
import { getDrawingSymbol } from "./symbols";
import { getBuffer } from "./utils";
import { ServerType } from "ol/source/wms";
import "./snapshots.scss";

const shadowMapId = "shadow-map";

export const generateSnapshots = (
    features: Feature[],
    basemap: Basemaps,
    setSnapshot: CallableFunction
): Promise<string> => {
    const source = new VectorSource({ features });
    const shadowMap = createShadowMap(basemap, source);
    const extent =
        features.length === 1 && features[0].getGeometry().getType() === "Point"
            ? getBuffer(features[0], 720)
            : source.getExtent();

    return new Promise((resolve) => {
        shadowMap.once("rendercomplete", () => {
            shadowMap.getView().fit(extent, {
                padding: [50, 50, 50, 50],
                callback: () =>
                    shadowMap.once("rendercomplete", () => {
                        const snapshot = getSnapshot(shadowMap);
                        setSnapshot(snapshot);
                        document.getElementById(shadowMapId).remove();
                        resolve(snapshot);
                    })
            });
        });
    });
};

const createShadowMap = (basemapId: Basemaps, source: VectorSource) => {
    const target = document.createElement("div");
    target.id = shadowMapId;
    document.body.append(target);

    return new Map({
        layers: [createBasemapLayer(basemapId), createLVMRoadsLayer(), createDrawingLayer(source)],
        target,
        view: new View({
            projection: config.default.projection,
            center: config.default.center,
            zoom: 3.55
        }),
        controls: defaultControls({ zoom: false, rotate: false })
    });
};

const createBasemapLayer = (basemapId: Basemaps) => {
    const basemapConfig = config.basemaps.find((b) => b.id === basemapId);
    if (basemapId === Basemaps.Orto) {
        const basemap = basemapConfig as IBasemapTileWMS;
        return new TileLayer({
            source: new TileWMS({
                url: basemap.source.url,
                params: basemap.source.params,
                projection: basemap.source.projection,
                serverType: basemap.source.serverType as ServerType,
                crossOrigin: basemap.source.crossOrigin
            })
        });
    }

    const basemap = basemapConfig as IBasemapTileXYZ;
    return new TileLayer({
        source: new XYZ({
            url: basemap.source.url + "/tile/{z}/{y}/{x}",
            maxZoom: basemap.source.maxZoom,
            projection: basemap.source.projection,
            tileGrid: new TileGrid(config.default.tileGrid),
            crossOrigin: basemap.source.crossOrigin
        })
    });
};

const createLVMRoadsLayer = () => {
    const layerConfig = config.layers.find((l) => l.id === "roadsLVM");

    return new ImageLayer({
        properties: { id: layerConfig.id },
        source: new ImageArcGISRest({
            url: layerConfig.url,
            crossOrigin: layerConfig.crossOrigin,
            attributions: layerConfig.copyright,
            params: layerConfig.params,
            imageLoadFunction: (image, src) =>
                ((image.getImage() as HTMLImageElement).src = src.replace("DPI=90", "DPI=96")),
            ratio: 1
        }),
        zIndex: layerConfig.zIndex
    });
};

const createDrawingLayer = (source: VectorSource) =>
    new VectorLayer({
        properties: { id: "shadow-drawing-layer" },
        source,
        style: (drawFeature) => getDrawingSymbol(drawFeature.get("DRAWTYPE"))
    });

const getSnapshot = (shadowMap: Map) => {
    const mapCanvas = document.createElement("canvas");
    const size = shadowMap.getSize();
    mapCanvas.width = size[0];
    mapCanvas.height = size[1];
    const mapContext = mapCanvas.getContext("2d");
    Array.prototype.forEach.call(document.querySelectorAll(".ol-layer canvas"), (canvas) => {
        if (canvas.width > 0) {
            const opacity = canvas.parentNode.style.opacity;
            mapContext.globalAlpha = opacity === "" ? 1 : Number(opacity);
            const transform = canvas.style.transform;
            // Get the transform parameters from the style's transform matrix
            const matrix = transform
                .match(/^matrix\(([^\(]*)\)$/)[1]
                .split(",")
                .map(Number);
            // Apply the transform to the export map context
            CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
            mapContext.drawImage(canvas, 0, 0);
        }
    });

    return mapCanvas.toDataURL();
};
